import videojs, {
  patchVideoJsPlayer,
  safePlay,
  isPlayerDisposed,
} from "@/lib/videojs-setup";
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import type { VideoJsPlayer } from "@/types/videojs-player";
import type { PlayerSubtitle } from "@/entry/server/movie";
import "@/styles/videojs-overrides.css";

type VideoJsCtx = {
  player: VideoJsPlayer | null;
  wrapperRef: React.RefObject<HTMLDivElement | null>;
};

const VideoJsContext = createContext<VideoJsCtx>({
  player: null,
  wrapperRef: { current: null },
});

export function useVideoJs() {
  return useContext(VideoJsContext);
}

function attachSubtitles(pl: VideoJsPlayer, subtitles: PlayerSubtitle[]) {
  if (!pl.addRemoteTextTrack || !pl.removeRemoteTextTrack || !pl.remoteTextTracks) {
    return;
  }

  var remote = pl.remoteTextTracks() as unknown as TextTrack[];
  for (var i = remote.length - 1; i >= 0; i -= 1) {
    pl.removeRemoteTextTrack(remote[i] as never);
    remote = pl.remoteTextTracks() as unknown as TextTrack[];
  }

  subtitles.forEach(function (sub, index) {
    var label = sub.dubbed ? sub.name + " (دوبله)" : sub.name;
    pl.addRemoteTextTrack!(
      {
        kind: "subtitles",
        label: label,
        srclang: "sub-" + String(index),
        src: sub.url,
        language: "sub-" + String(index),
        default: false,
      },
      false
    );
  });
}

type Props = {
  mediaSrc: string;
  mimeType: "application/x-mpegurl" | "video/mp4";
  subtitles: PlayerSubtitle[];
  wrapClassName?: string;
  videoClassNames?: string;
  children?: ReactNode;
};

export function VideoJsBridge({
  mediaSrc,
  mimeType,
  subtitles,
  wrapClassName,
  videoClassNames = "vjs-big-play-centered vjs-fill",
  children,
}: Props) {
  var containerRef = useRef<HTMLDivElement>(null);
  var wrapperRef = useRef<HTMLDivElement>(null);
  var playerRef = useRef<VideoJsPlayer | null>(null);
  var [player, setPlayer] = useState<VideoJsPlayer | null>(null);

  var mediaSrcRef = useRef(mediaSrc);
  mediaSrcRef.current = mediaSrc;
  var mimeTypeRef = useRef(mimeType);
  mimeTypeRef.current = mimeType;
  var pendingSeekTimeRef = useRef<number | null>(null);
  var pendingWasPausedRef = useRef<boolean | null>(null);

  var subtitlesKey = useMemo(
    function () {
      return subtitles
        .map(function (s) {
          return s.url + "|" + s.format;
        })
        .join("\n");
    },
    [subtitles]
  );

  useEffect(function () {
    var holder = containerRef.current;
    if (!holder) {
      return undefined;
    }

    var cancelled = false;

    holder.innerHTML = "";
    var videoEl = document.createElement("video");
    videoEl.className = ("video-js " + videoClassNames).trim();
    videoEl.setAttribute("playsinline", "true");
    videoEl.setAttribute("webkit-playsinline", "true");
    holder.appendChild(videoEl);

    var vPlayer = patchVideoJsPlayer(
      videojs(videoEl, {
        controls: false,
        autoplay: true,
        preload: "auto",
        techOrder: ["html5"],
        html5: {
          nativeTextTracks: false,
        },
      })
    );

    if (cancelled) {
      vPlayer.dispose();
      return undefined;
    }

    playerRef.current = vPlayer;
    setPlayer(vPlayer);

    vPlayer.src({
      src: mediaSrcRef.current,
      type: mimeTypeRef.current,
    });

    vPlayer.ready(function () {
      if (cancelled) {
        return;
      }
      safePlay(vPlayer);
    });

    return function () {
      cancelled = true;
      setPlayer(null);
      var p = playerRef.current;
      playerRef.current = null;
      if (p && !isPlayerDisposed(p)) {
        p.dispose();
      }
      if (holder) {
        holder.innerHTML = "";
      }
    };
  }, [videoClassNames]);

  useEffect(function () {
    var p = playerRef.current;
    if (!p || isPlayerDisposed(p) || !mediaSrc) {
      return;
    }

    var previousTime = p.currentTime();
    pendingSeekTimeRef.current =
      typeof previousTime === "number" && isFinite(previousTime)
        ? Math.max(0, previousTime)
        : null;
    pendingWasPausedRef.current = p.paused();

    p.src({ src: mediaSrc, type: mimeType });

    var restorePlaybackPosition = function () {
      var target = pendingSeekTimeRef.current;
      if (typeof target === "number" && isFinite(target)) {
        var du = p.duration();
        var clamped =
          typeof du === "number" && isFinite(du)
            ? Math.min(target, Math.max(0, du - 0.2))
            : target;
        p.currentTime(Math.max(0, clamped));
      }

      if (pendingWasPausedRef.current !== true) {
        safePlay(p);
      }

      pendingSeekTimeRef.current = null;
      pendingWasPausedRef.current = null;
      p.off("loadedmetadata", restorePlaybackPosition);
    };

    p.on("loadedmetadata", restorePlaybackPosition);
    p.ready(function () {
      if (pendingWasPausedRef.current === true) {
        return;
      }
      safePlay(p);
    });

    return function () {
      p.off("loadedmetadata", restorePlaybackPosition);
    };
  }, [mediaSrc, mimeType]);

  var attachSigRef = useRef("");
  useEffect(function () {
    attachSigRef.current = "";
  }, [mediaSrc]);

  useEffect(function () {
    var p = playerRef.current;
    if (!p || isPlayerDisposed(p)) {
      return;
    }

    var syncTracks = function () {
      var sig = mediaSrc + "|" + subtitlesKey;
      if (attachSigRef.current === sig) {
        return;
      }
      attachSigRef.current = sig;
      attachSubtitles(p, subtitles);
    };

    syncTracks();
    p.on("loadedmetadata", syncTracks);
    return function () {
      p.off("loadedmetadata", syncTracks);
    };
  }, [player, subtitles, subtitlesKey, mediaSrc]);

  var ctx = useMemo(
    function () {
      return {
        player: player,
        wrapperRef: wrapperRef,
      };
    },
    [player]
  );

  return (
    <VideoJsContext.Provider value={ctx}>
      <div
        ref={wrapperRef}
        className={"video-js-host " + (wrapClassName ? wrapClassName : "")}
      >
        <div ref={containerRef} className="video-js-host__stage" />
        {children}
      </div>
    </VideoJsContext.Provider>
  );
}
